import React, { useEffect, useState } from "react";
import ZTooltip from "@atoms/ZTooltip";
import ZSelect from "@atoms/ZSelect";
import ZTypography from "@atoms/ZTypography";
import { RefSelectProps } from "antd/lib/select";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import { capitalizeFirstLetter } from "@/Contracts/Utils";
import { IUserSelectProps } from "./interface";
import { ConnectedEmails, DKIMStatus } from "@/Contracts/Settings";
import flatMap from "lodash/flatMap";
import { IOptionGroupProps } from "@atoms/ZSelect/interface";

const { ZText } = ZTypography;

const ZUserSelect: React.FC<IUserSelectProps> = React.forwardRef(
	(props, ref) => {
		const selectRef = ref as React.RefObject<RefSelectProps>;
		const [customEmails, setCustomEmails] = useState<string[]>([]);
		const [customDomainEmails, setCustomDomainEmails] = useState<
			ConnectedEmails[]
		>([]);

		const {
			selectedUser,
			optGroup,
			filterCurrentUser,
			mode,
			allowEmailIntegratedUsers,
			allowDKIMEmails,
			shouldIncludeBotUsers,
			...selectProps
		} = props;

		const {
			companyUsersList,
			userId,
			email: integratedEmailUsers,
			dkim_verification
		} = useSelector((state: GlobalState) => ({
			companyUsersList: state.user.companyUserList,
			userId: state.user.userData.id,
			email: state.user.userCompany.integrations.email,
			dkim_verification:
				state.user.userCompany?.settings?.dkim_verification
		}));

		const usersList = filterCurrentUser
			? companyUsersList.filter(user => user.id !== userId)
			: companyUsersList;
		if (
			selectedUser &&
			!usersList.find(user => user.id === selectedUser.id)
		) {
			usersList.push(selectedUser);
		}

		const integratedEmails = integratedEmailUsers.map(user => user.email);

		useEffect(() => {
			if (allowDKIMEmails) {
				const customEmails = dkim_verification?.email
					.filter(email => email.status === DKIMStatus.Success)
					.map(email => email.email);

				const customDomainEmails = flatMap(
					dkim_verification?.domain.filter(
						domain => domain.status === DKIMStatus.Success
					),
					domain => domain.connected_emails
				).filter(
					connectedEmail =>
						connectedEmail.status === DKIMStatus.Success
				);

				if (customEmails) setCustomEmails(customEmails);
				if (customDomainEmails)
					setCustomDomainEmails(customDomainEmails);
			}
		}, []);

		const shouldShowVerifiedEmails =
			!!customEmails.length || !!customDomainEmails.length;

		const optionsGroup: IOptionGroupProps[] = [
			{
				label: "Users",
				selectOptions: usersList
					.filter((user: any) =>
						shouldIncludeBotUsers ? true : !user.bot_user_source
					)
					.map((companyUser: any, index: number) => {
						if (
							!integratedEmails.includes(companyUser.email) &&
							allowEmailIntegratedUsers &&
							mode !== "multiple"
						) {
							return {
								key: index,
								disabled: true,
								value: companyUser.id,
								name: (
									<ZTooltip
										showtooltip={true}
										placement="left"
										title="This user has not connected their inbox"
									>
										<ZText colorVariant={6} aria-disabled>
											{`${capitalizeFirstLetter(
												companyUser.name.full
											)} <${companyUser.email}>`}
										</ZText>
									</ZTooltip>
								)
							};
						} else {
							return {
								disabled: companyUser.is_disabled,
								key: index,
								value: companyUser.id,
								name: companyUser.is_disabled ? (
									<ZTooltip
										showtooltip
										placement="left"
										title="Inactive user"
									>
										<ZText colorVariant={6} aria-disabled>
											{`${capitalizeFirstLetter(
												companyUser.name.full
											)} <${companyUser.email}>`}{" "}
											( Inactive user )
										</ZText>
									</ZTooltip>
								) : (
									<ZText>{`${capitalizeFirstLetter(
										companyUser.name.full
									)} <${companyUser.email}>`}</ZText>
								)
							};
						}
					})
			}
		];

		if (optGroup) optionsGroup.unshift(...optGroup);

		// Show SES & DKIM emails
		if (props.allowDKIMEmails && shouldShowVerifiedEmails) {
			const emails = customEmails.map(email => {
				return {
					key: email,
					value: email,
					name: <span>{email}</span>
				};
			});

			const domainEmails = customDomainEmails.map(email => {
				return {
					key: email.name,
					value: email.email,
					name: <span>{`${email.name}<${email.email}>`}</span>
				};
			});

			const verifiedEmailsGroup: IOptionGroupProps = {
				label: "Verified emails",
				selectOptions: [...emails, ...domainEmails]
			};

			optionsGroup.push(verifiedEmailsGroup);
		}

		return !!props.optGroup ? (
			<ZSelect<string>
				zref={selectRef}
				getPopupContainer={trigger => trigger.parentNode}
				mode={mode}
				isOptionsGroup={true}
				optionsGroup={optionsGroup}
				useThreeLetterSearch
				{...selectProps}
			/>
		) : (
			<ZSelect<string>
				zref={selectRef}
				data-testid="user-select"
				mode={mode}
				useThreeLetterSearch
				getPopupContainer={trigger => trigger.parentNode}
				selectOptions={usersList.map(
					(companyUser: any, index: number) => ({
						disabled: companyUser.is_disabled,
						key: index,
						value: companyUser.id,
						name: companyUser.is_disabled ? (
							<ZTooltip
								showtooltip
								placement="left"
								title="Inactive user"
							>
								<ZText
									colorVariant={6}
									aria-disabled
								>{`${capitalizeFirstLetter(
									companyUser.name.full
								)} <${
									companyUser.email
								}> ( Inactive user )`}</ZText>
							</ZTooltip>
						) : (
							`${capitalizeFirstLetter(companyUser.name.full)} <${
								companyUser.email
							}>`
						)
					})
				)}
				{...selectProps}
			/>
		);
	}
);

export default ZUserSelect;
